import React from 'react';
import moment from "moment";

const numberCorrection = {
  "0": 0,
  "1": 5,
  "2": 4,
  "3": 3,
  "4": 2,
  "5": 1,
};

const useTextCorrection = () => {

  const filterNumber = (number) => {
    if(typeof number === "number") return (numberCorrection as any)[number.toString()];
    if(typeof number === "string") return (numberCorrection as any)[parseInt(number.split(' ')[0])]
    if(number === "" || number === undefined) return 0;
  };

  const replaceBreaks = (text) => {
    let elements = [];
    text.split(/&lt;BR&gt;|<BR>/).map((item, index) => {
      elements.push(<p>{filterText(item)}</p>)
    });
    return elements;
  };

  const filterText = (text: string) => {
    if(typeof text != "string") return text;
    return text.replace(/&amp;/g,"&").replace(/&lt;BR&gt;|<BR>/g,"\n");
  };

  const isEmptyValue = (value) => {
    if(value === undefined || value === null) return true;
    return typeof value === "string" && value.trim() === "";
  };

  // PORTAL-174. Letters only, no macrons (ā ē ī ō ū) - splitting a macron word into separate
  // regex matches at the macron character mis-capitalised every syllable after it (e.g.
  // "Ngātahi" -> "NgāTahi"). The class below treats a macron vowel as an ordinary letter so the
  // whole word matches in one run.
  const WORD_CHAR = "A-Za-zĀāĒēĪīŌōŪū";

  // A run that is already mixed case (some but not all letters upper) is trusted as-is rather
  // than force-lowercased - the old rule flattened every word to Titlecase regardless, which
  // destroyed real names that arrive correctly cased from Pronto: "McFarlane" -> "Mcfarlane",
  // "MidCentral" -> "Midcentral", "iVolt" -> "Ivolt". Only a run that starts genuinely
  // ALL-LOWERCASE gets reshaped, so a name typed in full lowercase still comes out right.
  const reshapeWord = (word: string): string => {
    if(word === word.toUpperCase()) return word; // acronym, e.g. "NZ", "UPS" - untouched
    if(word !== word.toLowerCase()) return word; // already mixed case - trust the source

    // "Mc"/"Mac" surname prefixes need the letter right after the prefix capitalised too
    // ("mcdonald" -> "McDonald", not "Mcdonald") - the generic capitalise-first rule below
    // would otherwise lowercase exactly the letter that has to stay capital.
    const mc = word.match(/^mc([a-z])/);
    if(mc && word.length > 2) return "Mc" + mc[1].toUpperCase() + word.slice(3);

    // "mac" collides with ordinary English words ("machine", "macro", "mace"), not just
    // surnames (MacDonald, MacArthur) - there's no structural way to tell them apart, so this
    // is a data-derived exception list, not a dictionary. Found by scanning real Pronto text:
    // "Pipe threading machine" was coming out as "Pipe Threading MacHine".
    const MAC_EXCEPTIONS = new Set(["machine", "machines", "machinery", "macchine", "macuata"]);
    const mac = word.match(/^mac([a-z])/);
    if(mac && word.length > 3 && !MAC_EXCEPTIONS.has(word)) return "Mac" + mac[1].toUpperCase() + word.slice(4);

    return word.charAt(0).toUpperCase() + word.slice(1);
  };

  // Suffixes after an apostrophe that are a contraction, not a name boundary - "don't", "it's",
  // "Nando's" all keep the letter after the apostrophe lowercase. Anything else after an
  // apostrophe (Ō'Connor, D'Souza) is treated as the start of a new name segment and is
  // capitalised the same as any other word.
  const CONTRACTION_SUFFIXES = new Set(["s", "t", "d", "m", "re", "ve", "ll"]);

  const reshapeToken = (token: string): string => {
    if(!token.includes("'")) return reshapeWord(token);
    const parts = token.split("'");
    if(parts.length === 2 && CONTRACTION_SUFFIXES.has(parts[1].toLowerCase())) {
      return reshapeWord(parts[0]) + "'" + parts[1].toLowerCase();
    }
    return parts.map(reshapeWord).join("'");
  };

  const titleCase = (text: string) => {
    if(typeof text != "string" || text === "") return text;
    // Match letter runs (plus internal apostrophes, e.g. "Nando's", "O'Connor") only - the
    // previous regex matched a "word" as \w followed by \S*, which swallows a "/" and everything
    // after it into ONE match, so "Gardening/Landscaping" only had its first letter capitalised
    // and the rest (including the second half) lowercased.
    const wordPattern = new RegExp(`[${WORD_CHAR}]+(?:'[${WORD_CHAR}]+)*`, "g");
    return text.replace(wordPattern, (match, offset) => {
      // A letter run glued straight to a digit with no space ("70mm", "M6x12mm") is a unit or
      // part-number suffix, not a real word - capitalising it ("70mm" -> "70Mm") was wrong.
      // Only a run that starts at a genuine word boundary gets reshaped.
      const prevChar = offset > 0 ? text.charAt(offset - 1) : "";
      if(/[0-9]/.test(prevChar)) return match;
      return reshapeToken(match);
    });
  };

  // PORTAL-174. For genuinely free-typed sentences (e.g. CallDescription) - titleCase() capitalises
  // every word, which reads wrong on a sentence ("Tap In N6 Is Flashing Red Light") and would
  // destroy embedded codes/acronyms it can't tell apart from ordinary words ("MCC", "ICAMHS").
  // Real Sentence Case only capitalises the sentence's own first letter and leaves the rest of
  // the text - including whatever casing the acronyms and codes already have - exactly as typed.
  const sentenceCase = (text: string) => {
    if(typeof text != "string" || text === "") return text;
    // Only the string's own first character - a sentence that starts with a number ("6 monthly
    // service...") has no letter to capitalise there, and reaching forward to the first letter
    // instead would capitalise the WRONG word ("6 Monthly service..."), not fix anything.
    if(!/[A-Za-zĀāĒēĪīŌōŪū]/.test(text.charAt(0))) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const filterEmpty = (field) => {
    return <td>{filterText(field)}</td>
  };

  const fillSpacing = (text: string) => {
    let newText = text;
    if(text.length < 10); {
      for(let i =0; i<(10-text.length); i++) {
        newText = " ".concat(newText);
      }
    }
    return newText;
  };

  const correctDateFormat = (date: string, header: string) => {
    if(!header.toLowerCase().includes('date')) return date;
    if(date == '00:00:00 ') return 'Date not populated';
    let originalFormat = 'YYYY-MM-DD';
    let format = 'DD-MM-YYYY';

    if(header.toLowerCase().includes('routine')) {
      originalFormat = 'YYYY-MM';
      format = 'MM-YYYY'
    } else if(header.toLowerCase().includes('sla')) {
      originalFormat = 'hh:mm:ss YYYY-MM-DD';
      format = 'hh:mm:ss DD-MM-YYYY'
    }

    return moment(date, originalFormat).format(format);
  };

  return {
    filterNumber,
    filterText,
    filterEmpty,
    isEmptyValue,
    titleCase,
    sentenceCase,
    replaceBreaks,
    fillSpacing,
    correctDateFormat,
  }
};

export default useTextCorrection
